import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "../../components/Sections/HeroSection";
import StatisticsSection from "../../components/Sections/StatisticsSection";
import ServicesSection from "../../components/Sections/ServicesSection";
import FeaturesSection from "../../components/Sections/FeaturesSection";
import ProcessSection from "../../components/Sections/ProcessSection";
import ToolsSection from "../../components/Sections/ToolsSection";
import BenefitsSection from "../../components/Sections/BenefitsSection";
import CTASection from "../../components/Sections/CTASection";
import HorizontalScroller from "../../components/Sections/HorizontalScroller";
import { NextSeo } from "next-seo";
import { Feature, ProcessStep, ScrollerItem } from "../../utils/types";

const healthcare: React.FC = () => {
  const statistics = [
    {
      title: "Increase in Patient Satisfaction",
      description:
        "Improved efficiency and communication led to happier patients.",
      value: "35%",
    },
    {
      title: "Decrease in Appointment No-Shows",
      description:
        "Smart reminders and easy rescheduling reduced missed appointments.",
      value: "25%",
    },
    {
      title: "Boost in Telemedicine Consultations",
      description:
        "Our telemedicine solutions saw a dramatic increase in virtual visits.",
      value: "300%",
    },
    {
      title: "Improvement in Diagnostic Accuracy",
      description: "AI-assisted diagnostics improved overall accuracy rates.",
      value: "15%",
    },
  ];

  const healthcareServices = [
    {
      title: "Custom Healthcare Software Development",
      description:
        "Imagine a digital health solution so tailored to your needs, it fits your practice better than a perfectly sized lab coat.",
    },
    {
      title: "Electronic Health Record (EHR) Systems",
      description:
        "Picture an EHR system so intuitive, even your most tech-averse staff will think it's just what the doctor ordered.",
    },
    {
      title: "Telemedicine App Development",
      description:
        "Experience a virtual clinic so seamless, your patients will forget they're not actually in your office (pajamas optional).",
    },
    {
      title: "AI-Powered Diagnostic Assistants",
      description:
        "Harness AI tools that analyze symptoms faster than you can say 'Dr. House', helping you make accurate diagnoses in record time.",
    },
    {
      title: "Patient Engagement Platforms",
      description:
        "Engage your patients with interactive portals more captivating than a binge-worthy medical drama series.",
    },
    {
      title: "Medical Imaging Analysis Software",
      description:
        "Leverage AI-powered imaging tools so sharp, they spot anomalies quicker than a radiologist after their third espresso.",
    },
    {
      title: "Healthcare Data Analytics",
      description:
        "Transform your medical data into a crystal ball, revealing insights about patient outcomes, operational efficiency, and more.",
    },
    {
      title: "Pharmacy Management Systems",
      description:
        "Utilize inventory systems so smart, they predict medication needs faster than you can say 'prescription refill'.",
    },
    {
      title: "Appointment Scheduling & Management",
      description:
        "Streamline your scheduling process with an AI that juggles appointments better than a circus performer with a PhD in time management.",
    },
  ];

  const features: Feature[] = [
    {
      title: "GenAI-Powered Medical Coding",
      description:
        "Automate the medical coding process using AI to analyze clinical documentation and assign accurate codes, reducing administrative burden and improving billing accuracy.",
      imageUrl: "/icons/AIpowered_diagnostic.png", // Replace with your actual SVG path
    },
    {
      title: "Predictive Analytics for Patient Care",
      description:
        "Leverage AI to analyze patient data and predict health risks, enabling proactive care and personalized treatment plans.",
      imageUrl: "/icons/risk_assessment.png", // Replace with your actual SVG path
    },
    {
      title: "Intelligent Appointment Scheduling",
      description:
        "Optimize your clinic's schedule with AI that considers patient needs, doctor availability, and even traffic patterns.",
      imageUrl: "/icons/Appointment.png", // Replace with your actual SVG path
    },
    {
      title: "Generative AI for Medical Imaging",
      description:
        "Enhance diagnostic accuracy with AI that can interpret medical images, identify abnormalities, and generate detailed reports.",
      imageUrl: "/icons/labreport.png", // Replace with your actual SVG path
    },
    {
      title: "Natural Language Processing for EHRs",
      description:
        "Transform unstructured clinical notes into actionable data, making your Electronic Health Records work smarter, not harder.",
      imageUrl: "/icons/nlp.png", // Replace with your actual SVG path
    },
    {
      title: "Personalized Treatment Plans",
      description:
        "Generate tailored treatment recommendations based on patient history, genetic data, and the latest medical research.",
      imageUrl: "/icons/personalized.png", // Replace with your actual SVG path
    },
  ];

  const steps: ProcessStep[] = [
    {
      title: "Hospitals and Clinics",
      description:
        "Streamlining operations and enhancing patient care in medical facilities of all sizes.",
      icon: "/icons/image.png",
    },
    {
      title: "Telemedicine Providers",
      description:
        "Powering virtual healthcare delivery with robust, user-friendly platforms.",
      icon: "/icons/telemedicine.png",
    },
    {
      title: "Diagnostic Laboratories",
      description:
        "Optimizing lab processes and improving accuracy with AI-driven analysis.",
      icon: "/icons/Information.png",
    },
    {
      title: "Pharmacies",
      description:
        "Enhancing medication management and customer service in retail and hospital pharmacies.",
      icon: "/icons/Pharmacy.png",
    },
    {
      title: "Mental Health Services",
      description:
        "Supporting mental health professionals with tailored software solutions.",
      icon: "/icons/15_24hr.png",
    },
    {
      title: "Elderly Care Facilities",
      description:
        "Improving care coordination and resident monitoring in nursing homes and assisted living facilities.",
      icon: "/icons/19_Webcam.png",
    },
  ];

  const tools = [
    {
      name: "Python",
      icon: "/icons/python1.svg",
    },
    {
      name: "TensorFlow",
      icon: "/icons/TF.png",
    },
    {
      name: "PyTorch",
      icon: "/icons/pytorch.svg",
    },
    {
      name: "React",
      icon: "/icons/react.svg",
    },
    {
      name: "Node.js",
      icon: "/icons/nodeJS.svg",
    },
    {
      name: "PostgreSQL",
      icon: "/icons/postgresql.png",
    },
    {
      name: "MongoDB",
      icon: "/icons/MongoDB.png",
    },
    {
      name: "FHIR",
      icon: "/icons/fhir.png",
    },

    {
      name: "Kubernetes",
      icon: "/icons/kubernetes.png",
    },
    {
      name: "GraphQL",
      icon: "/icons/GraphQL.png",
    },
    {
      name: "Progressive WebApp",
      icon: "/icons/PWA.png",
    },
    {
      name: "OpenAI",
      icon: "/icons/openai.png",
    },
  ];

  const benefits = [
    {
      title: "AI Maestros",
      description:
        "Enhance diagnostic accuracy with our AI algorithms. We make complex cases look like a walk in the park!",
    },
    {
      title: "Patient Engagement",
      description:
        "Boost patient satisfaction with our tailored engagement strategies. We turn patients into your biggest fans.",
    },
    {
      title: "Interoperability Gurus",
      description:
        "Seamlessly connect all your healthcare systems. We make data flow smoother than a well-oiled stethoscope.",
    },
    {
      title: "Workflow Optimizers",
      description:
        "Streamline clinical workflows with our savvy solutions. We turn chaos into a well-choreographed symphony!",
    },
    {
      title: "Compliance Champions",
      description:
        "Navigate regulatory waters with ease. We turn HIPAA and GDPR into your best friends.",
    },
    {
      title: "Telemedicine Wizards",
      description:
        "Maximize virtual care with our telemedicine prowess. We turn every online consultation into gold-standard care.",
    },
    {
      title: "Predictive Analytics Oracles",
      description:
        "Forecast patient outcomes and optimize resource allocation. No more guesswork, just data-driven decisions!",
    },
    {
      title: "Health Tech Futurists",
      description:
        "Stay ahead of healthcare tech trends effortlessly. We future-proof your practice faster than you can say 'innovation'.",
    },
  ];

  const scrollerItems: ScrollerItem[] = [
    { title: "AI Diagnostics", icon: "/icons/01_Prescriptions.png" },
    { title: "Telemedicine", icon: "/icons/Telemedicine.png" },
    { title: "EHR Integration", icon: "/icons/EHR.png" },
    { title: "Patient Engagement", icon: "/icons/patient.png" },
    { title: "Clinical Decision Support", icon: "/icons/clinic.png" },
    { title: "Health Data Analytics", icon: "/icons/data_analytics.png" },
    { title: "Remote Patient Monitoring", icon: "/icons/remote.png" },
    { title: "HIPAA Compliance", icon: "/icons/hipaa.png" },
    { title: "Interoperability", icon: "/icons/interoperatibility.png" },
    { title: "Predictive Healthcare", icon: "/icons/20_Alert.png" },
  ];

  return (
    <>
      <NextSeo
        title="Healthcare Software Solutions | PlanetHive AI"
        canonical="https://www.planethive.ai/industries/healthcare"
        description="Revolutionize your healthcare practice with our AI-powered solutions and embrace the future of healthcare technology."
        openGraph={{
          url: "https://www.planethive.ai/industries/healthcare",
          title: "Healthcare Software Solutions | PlanetHive AI",
          description:
            "Revolutionize your healthcare practice with our AI-powered solutions. Streamline operations, enhance patient care, and embrace the future of healthcare technology.",
          images: [
            {
              url: "/images/planet-hive-logo.png",
              width: 421,
              height: 120,
              alt: "Healthcare Software Solutions",
            },
          ],
        }}
      />
      <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Healthcare Software Solutions | PlanetHive AI",
      description:
        "Revolutionize your healthcare practice with our AI-powered solutions. Streamline operations, enhance patient care, and embrace the future of healthcare technology.",
      url: "https://www.planethive.ai/industries/healthcare",
      mainEntity: {
        "@type": "Service",
        name: "Healthcare Software Solutions",
        description:
          "AI-powered healthcare solutions tailored for hospitals, clinics, telemedicine providers, and elderly care facilities. Enhance patient care, optimize operations, and ensure regulatory compliance with advanced AI-driven tools.",
        provider: {
          "@type": "Organization",
          name: "Planet Hive",
          url: "https://www.planethive.ai",
          logo: "https://www.planethive.ai/images/planet-hive-logo.png",
        },
        areaServed: {
          "@type": "Place",
          name: "Global",
        },
        audience: {
          "@type": "Audience",
          audienceType: [
            "Hospitals",
            "Clinics",
            "Telemedicine Providers",
            "Diagnostic Laboratories",
            "Pharmacies",
            "Mental Health Services",
            "Elderly Care Facilities",
          ],
        },
        availableChannel: {
          "@type": "ServiceChannel",
          serviceUrl: "https://www.planethive.ai/contact",
        },
        termsOfService: "https://www.planethive.ai/terms-conditions",
        keywords: [
          "AI healthcare solutions",
          "telemedicine",
          "patient engagement",
          "medical imaging AI",
          "EHR integration",
          "predictive healthcare analytics",
          "healthcare compliance",
        ],
      },
      potentialAction: {
        "@type": "ContactAction",
        name: "Get in Touch with Planet Hive",
        target: "https://www.planethive.ai/contact",
      },
      video: [
        {
          "@type": "VideoObject",
          name: "Transforming Healthcare with AI-Powered Solutions",
          description:
            "Discover how Planet Hive’s AI-driven tools empower healthcare providers to streamline operations, enhance patient care, and stay compliant.",
          thumbnailUrl:
            "https://www.planethive.ai/images/healthcare-video-thumb.jpg",
          uploadDate: "2024-01-22",
          contentUrl: "/video/healthcare.mp4",
          embedUrl: "/video/healthcare.mp4",
          duration: "PT3M30S",
        },
      ],
      mainEntityOfPage: {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name:
              "How can AI improve diagnostic accuracy in healthcare practices?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "AI enhances diagnostic accuracy by analyzing medical images, patient history, and clinical notes with precision. This helps healthcare providers detect anomalies and recommend treatments faster and more reliably.",
            },
          },
          {
            "@type": "Question",
            name: "What types of healthcare providers benefit from Planet Hive's solutions?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Planet Hive’s solutions cater to a wide range of providers, including hospitals, clinics, telemedicine platforms, pharmacies, and elderly care facilities. Our AI-powered tools are designed to streamline operations and improve patient care across the board.",
            },
          },
          {
            "@type": "Question",
            name: "How do AI tools improve patient engagement?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "AI-powered patient engagement platforms provide personalized reminders, interactive portals, and predictive insights, enabling patients to stay proactive about their health while fostering stronger relationships with providers.",
            },
          },
          {
            "@type": "Question",
            name: "How does AI assist with telemedicine solutions?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "AI powers telemedicine by offering features like intelligent appointment scheduling, symptom analysis, and secure document handling. This ensures a seamless virtual consultation experience for both patients and healthcare providers.",
            },
          },
          {
            "@type": "Question",
            name: "How does Planet Hive address healthcare compliance and data security?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Planet Hive integrates advanced security protocols and compliance frameworks (HIPAA, GDPR) to protect sensitive patient data. Our solutions ensure data integrity while meeting regulatory requirements globally.",
            },
          },
        ],
      },
    }),
  }}
/>


      <Header />
      <main className="bg-[#10031dff] text-white">
        <HeroSection
          mediaType="video"
          mediaUrl="/video/healthcare.mp4"
          title="Planet Hive: Your Prescription for Healthcare Innovation"
          description="Empowering healthcare providers with AI-powered solutions to streamline workflows, elevate patient care, and conquer the digital age."
          buttonText="Join Us Today"
          buttonLink="/contact"
        />
        <ServicesSection
          title="Is your healthcare practice stuck in the digital Stone Age?"
          description="Let's face it: in a world where AI can write sonnets and cars can drive themselves, why are you still drowning in paperwork and outdated systems? It's time for a change, and Planet Hive is here to lead the charge."
          services={healthcareServices}
        />

        <StatisticsSection
          title="Key Statistics"
          description="The Proof is in the Patient Outcomes. Don't just take our word for it. Our Healthcare solutions have helped providers achieve:"
          statistics={statistics}
        />
        <FeaturesSection
          title="Power Up Your Healthcare Practice<br>With AI Magic (Digital Doctor's Assistant Included)"
          features={features}
        />
        <ProcessSection
          title="Whom Do We Cater To?"
          description="We cater to a wide range of healthcare providers, including"
          steps={steps}
        />
        <HorizontalScroller items={scrollerItems} />
        <ToolsSection
          title="Our Tech Stack<br> Where Medical Innovation Meets Technological Mastery!"
          tools={tools}
        />
        <BenefitsSection
          title="Why Choose Planet Hive?<br>We're Not Just Techies, We're Healthcare Problem Solvers!"
          benefits={benefits}
        />
        <CTASection
          modelUrl="/spaceRobotanimate.glb" // Replace with your 3D model path
          title="Ready to Transform Your Healthcare Practice?"
          description="Get in touch for a free consultation and see how we can revolutionize your patient care!"
        />
      </main>
      <Footer />
    </>
  );
};

export default healthcare;
